/**
 * HuddleBanner — AI-generated practice huddle brief.
 *
 * - Icons and markdown bold (**text**) are provided by the backend in Phase 2.
 *   In Phase 1 they are embedded in SEED_ITEMS for demo purposes.
 * - RefreshCw regenerates the brief (Phase 2: swap for AI API call).
 * - "Share as Agenda" opens the browser email client via mailto:.
 * - Collapsible; collapse state persists to sessionStorage.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, Send, AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

// Icon component per signal type — Phase 2: driven by backend icon field
const ICON_MAP = {
  critical: <AlertCircle size={15} color="var(--red-critical)" style={{ flexShrink: 0, marginTop: 2 }} />,
  watch:    <AlertTriangle size={15} color="var(--yellow)" style={{ flexShrink: 0, marginTop: 2 }} />,
  ok:       <CheckCircle2 size={15} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />,
  info:     <Info size={15} color="var(--aqua)" style={{ flexShrink: 0, marginTop: 2 }} />,
};

// Seed data — Phase 2: replace with backend response.
// Text supports **bold** markdown; icon type maps to lucide icon above.
const SEED_ITEMS = [
  { type: 'critical', text: '**PM shift staffing gap — 2 RNs short.** Escalate to float pool by 2 PM.' },
  { type: 'watch',    text: '**Diabetic A1C outreach at 68%** — 7% below monthly target. Review cohort list.' },
  { type: 'watch',    text: '**Same-day access trending down** this week (78% vs 85% target).' },
  { type: 'ok',       text: '**Quality audit prep complete** for Oncology — no open items.' },
  { type: 'ok',       text: '**Patient satisfaction stable** at 4.3/5.0 — above benchmark.' },
  { type: 'info',     text: '**Medication adherence follow-ups:** 12 pending authorizations due this week.' },
  { type: 'info',     text: '**Post-discharge calls:** 8 patients due before Friday.' },
];

/** Render text with **bold** markdown as <strong> nodes. */
function renderMarkdown(text) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ fontWeight: 600, color: 'var(--navy-deep)' }}>{part}</strong>
      : part
  );
}

function generateItems() {
  // Phase 2: replace with await fetch('/api/ai/huddle-brief')
  return [...SEED_ITEMS];
}

export default function HuddleBanner() {
  const [isOpen, setIsOpen] = useState(() => {
    try { return sessionStorage.getItem('nn_huddle_open') !== 'false'; } catch { return true; }
  });
  const [items, setItems] = useState(generateItems);

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    try { sessionStorage.setItem('nn_huddle_open', String(next)); } catch {}
  };

  const regenerate = () => {
    setItems((prev) => [...prev].sort(() => Math.random() - 0.5));
  };

  const shareAsAgenda = () => {
    const body = items
      .map((it, i) => `${i + 1}. ${it.text.replace(/\*\*/g, '')}`)
      .join('\n');
    const subject = encodeURIComponent("Today's Practice Huddle Agenda");
    window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(`Today's Huddle Agenda:\n\n${body}\n\n— Shared from NexoNurse Practice Pulse`)}`;
  };

  return (
    <div
      style={{
        background: 'var(--white)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-card)',
        overflow: 'hidden',
        animation: 'fadeUp 0.3s ease both',
      }}
    >
      {/* Header row — entire div is clickable for expand/collapse */}
      <div
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Collapse huddle brief' : 'Expand huddle brief'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '13px 18px',
          borderBottom: isOpen ? '1px solid var(--surface)' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-deep)' }}>
          Huddle Brief
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isOpen && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); regenerate(); }}
              title="Regenerate brief"
              aria-label="Regenerate huddle brief"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-dark-secondary)', padding: '5px 6px',
                borderRadius: 6, display: 'flex', alignItems: 'center',
              }}
            >
              <RefreshCw size={14} />
            </button>
          )}
          <div
            style={{
              color: 'var(--text-dark-secondary)', padding: '5px 6px',
              borderRadius: 6, display: 'flex', alignItems: 'center',
            }}
          >
            {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </div>
        </div>
      </div>

      {/* Item list — one paragraph block, no separators */}
      {isOpen && (
        <>
          <div style={{ padding: '12px 18px 8px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                {ICON_MAP[item.type] || ICON_MAP.info}
                <span style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.6 }}>
                  {renderMarkdown(item.text)}
                </span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 18px 13px',
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              AI-generated from live domain signals. Review before sharing.
            </span>
            <button
              type="button"
              onClick={shareAsAgenda}
              style={{
                fontSize: 11.5, fontWeight: 600, color: 'var(--aqua)',
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, padding: 0,
              }}
            >
              <Send size={11} />
              Share as Agenda
            </button>
          </div>
        </>
      )}
    </div>
  );
}
